import { createSlice } from '@reduxjs/toolkit';
import { mapMenuTabKeys, mapModes } from 'client/utils/constants/appStates';
import { mapResize } from 'client/utils/map/mapResizeUtil';

const { DEFAULT, AOI, MODIFYING, ADDING_OBSERVATION, LOADING } = mapModes;
const { LAYERS } = mapMenuTabKeys;

export const mapSlice = createSlice({
    name: 'map',
    initialState: {
        mode: [DEFAULT],
        selected: [],
        selectedRedactedAsset: null,
        openContactDetailsTab: false,
        menuOpen: false,
        menuSelectedTab: LAYERS,
        aoiError: null,
        hideBasemap: false,
        ownerId: null,
        isolatedViewsAssetCategory: null,
        showCustomBackdrops: false,
        showAnnotations: true,
        showDimensions: true,
        declutterCustomVectors: false,
        trackLocation: false,
        myLocation: null,
        highContrast: null,
        showDynamicLegend: false
    },
    reducers: {
        setMapModes: (state, action) => {
            const modes = action?.payload;
            if (!Array.isArray(modes)) {
                // eslint-disable-next-line no-console
                console.error('Array expected for setMapModes method');
                return;
            }
            return { ...state, mode: modes };
        },
        addMapMode: (state, action) => {
            const mode = action?.payload;
            const modes = [...state.mode];
            if (mode && !modes.includes(mode)) {
                modes.push(mode);
            }
            return { ...state, mode: modes };
        },
        removeMapMode: (state, action) => {
            const mode = action?.payload;
            const modes = [...state.mode].filter(currentMode => currentMode !== mode);
            return { ...state, mode: modes };
        },
        resetMapModes: (state, action) => {
            return { ...state, mode: [DEFAULT] };
        },
        setSelected: (state, action) => {
            const selected = action?.payload;
            return { ...state, selected: selected, selectedRedactedAsset: null, openContactDetailsTab: false };
        },
        setSelectedOpenContactDetails: (state, action) => {
            const selected = action?.payload;
            return { ...state, selected: selected, selectedRedactedAsset: null, openContactDetailsTab: true };
        },
        setSelectedRedactedAsset: (state, action) => {
            const selectedRedactedAsset = action?.payload;
            return { ...state, selectedRedactedAsset: selectedRedactedAsset, selected: [] };
        },
        setMenuOpen: (state, action) => {
            // We have to trigger a map resize when opening or closing the menu
            // this fixes a bug where the map slides off too far to the right when opening the menu
            // or leaves a blank space on the right when closing the menu.
            mapResize();
            return { ...state, menuOpen: action?.payload };
        },
        setMenuSelectedTab: (state, action) => {
            return { ...state, menuSelectedTab: action?.payload };
        },
        setAOIError: (state, action) => {
            return { ...state, aoiError: action?.payload };
        },
        setIsolatedViewsOwnerId: (state, action) => {
            return { ...state, ownerId: action?.payload };
        },
        setIsolatedViewsAssetCategory: (state, action) => {
            return { ...state, isolatedViewsAssetCategory: action?.payload ?? null };
        },
        setCustomBackdropShowing: (state, action) => {
            return { ...state, showCustomBackdrops: Boolean(action?.payload) };
        },
        setAnnotationsShowing: (state, action) => {
            return { ...state, showAnnotations: Boolean(action?.payload) };
        },
        setDimensionsShowing: (state, action) => {
            return { ...state, showDimensions: Boolean(action?.payload) };
        },
        setDeclutterCustomVectors: (state, action) => {
            return { ...state, declutterCustomVectors: Boolean(action?.payload) };
        },
        setHideBasemap: (state, action) => {
            return { ...state, hideBasemap: Boolean(action?.payload) };
        },
        setTrackLocation: (state, action) => {
            return { ...state, trackLocation: Boolean(action?.payload), gotGeoLocationResult: false };
        },
        setGotGeoLocationResult: (state, action) => {
            // should always be called with true. This is to indicate that geolocation has resolved with a location or error
            // so that we can make 'get my location' buttons show a loading state
            // in between dispatching setTrackLocation and actually getting the location
            return { ...state, gotGeoLocationResult: Boolean(action?.payload) };
        },
        setHighContrast: (state, action) => {
            return { ...state, highContrast: Boolean(action?.payload) };
        },
        setShowDynamicLegend: (state, action) => {
            return { ...state, showDynamicLegend: Boolean(action?.payload) };
        }
    }
});

export const selectMapMode = state => state.map.mode;
export const selectShowDynamicLegend = state => state?.map?.showDynamicLegend ?? false;
export const selectAOIMode = state => state?.map?.mode.includes(AOI) && !state?.map?.mode.includes(MODIFYING) && !state?.map?.mode.includes(ADDING_OBSERVATION);

export const selectLoadingAOI = state => state?.map?.mode.includes(AOI) && state?.map?.mode.includes(LOADING);

export const isExactMapMode = (activeModes, expectedModes) =>
    activeModes.length === expectedModes.length && expectedModes.every(mode => activeModes.includes(mode));

export const {
    setMapModes, addMapMode, removeMapMode, resetMapModes,
    setSelected, setSelectedOpenContactDetails, setSelectedRedactedAsset, setMenuOpen, setMenuSelectedTab, setAOIError, setIsolatedViewsOwnerId,
    setIsolatedViewsAssetCategory,
    setDeclutterCustomVectors, setCustomBackdropShowing, setAnnotationsShowing, setDimensionsShowing,
    setHideBasemap, setTrackLocation, setGotGeoLocationResult, setHighContrast, setShowDynamicLegend
} = mapSlice.actions;

export default mapSlice.reducer;
